/**
 * 引擎核心类型定稿。见 docs/ARCHITECTURE.md §4.5 / §4.6 / §4.8 / §4.17 / §4.18 / §4.19。
 *
 * 本文件是 poker/ 下所有模块的共同依赖，只放类型 + 极少量零依赖的常量与构造器。
 *
 * 三条贯穿全文件的红线：
 *  - #17 筹码全是整数（`Chips`），最小单位 1，全系统禁用浮点筹码。
 *  - #18 `to` 的语义恒为「本轮累计投入总额」（raise-to），绝不是增量。
 *  - #21 `LegalActions.canRaise` 是能否加注的唯一判据。
 *
 * 可变性约定：`readonly` 表示「整手牌不变」或「对外只读」；引擎在 draft 上原地推进的字段
 * （stack / status / phase / deckIndex …）刻意不加 readonly。铁律 #3 仍然成立：
 * 入参 state 视为 immutable，改的只能是自己 clone 出来的 draft。
 */
import type { AccountId, CardId, Chips, HandId, SeatIndex } from '../ids.js'
import type {
  AnteMode,
  AssistPolicy,
  MissedBlindPolicy,
  NewPlayerPolicy,
  ShowdownPolicy,
} from '../config/roomConfig.js'
import type { RankIndex } from './cards.js'

// ============================================================================
// 牌型评估（§4.2）
// ============================================================================

export enum HandCategory {
  HighCard = 0,
  Pair = 1,
  TwoPair = 2,
  Trips = 3,
  Straight = 4,
  Flush = 5,
  FullHouse = 6,
  Quads = 7,
  StraightFlush = 8,
}

/**
 * 玩法：标准德州（52 张）或短牌（36 张，去掉 2-5；同花大于葫芦；A-6-7-8-9 是最小的顺子）。
 * 整手牌、整个房间固定一种，不会在同一次比较里混用。
 */
export type DeckVariant = 'standard' | 'shortDeck'

export const DECK_VARIANTS: readonly DeckVariant[] = ['standard', 'shortDeck']

/**
 * 24 bit 整数编码：`(category << 20) | (r1 << 16) | (r2 << 12) | (r3 << 8) | (r4 << 4) | r5`。
 * 理论最大 9,227,468 < 2^24，安全落在 int32 位运算与 float64 精确整数区间。
 *
 * 短牌另置第 24 位，并把 category 字段换成**短牌里的强弱序**（同花 6、葫芦 5），
 * 于是同一手短牌里直接比数值依然成立；显示牌型名一律走 `categoryOf`，它会换回来。
 * 最大 25 位，仍在 int32 安全区间。
 *
 * **两手牌 value 完全相等 ⇔ 摊牌平分**，不得引入任何花色破平逻辑。
 * 打成 branded type 是为了让「把随便一个 number 当牌力比大小」在编译期就失败。
 */
export type HandRankValue = number & { readonly __brand: 'HandRankValue' }

export interface HandRank {
  readonly value: HandRankValue
  readonly category: HandCategory
  /** 有效 slot，长度 1..5（各 category 的有效位数见 §4.2） */
  readonly ranks: readonly RankIndex[]
  /** 确定性输出，供 UI 高亮 */
  readonly bestFive: readonly CardId[]
}

// ============================================================================
// 阶段与状态（§4.5）
// ============================================================================

export const HAND_STATE_SCHEMA_VERSION = 1

export type Street = 'preflop' | 'flop' | 'turn' | 'river'

export type HandPhase =
  | 'dealing'
  | 'preflop'
  | 'flop'
  | 'turn'
  | 'river'
  | 'showdown'
  | 'payout'
  | 'complete'

/** 可以行动的四个阶段。其取值与 Street 逐一重合，故可用作 Street 的类型守卫。 */
export const BETTING_PHASES = ['preflop', 'flop', 'turn', 'river'] as const

export function isBettingPhase(phase: HandPhase): phase is Street {
  return (
    phase === 'preflop' || phase === 'flop' || phase === 'turn' || phase === 'river'
  )
}

export type PlayerHandStatus = 'active' | 'folded' | 'allIn'

/**
 * 位置标签。`seatOrder` = 从翻前第一个行动者起的下标（§4.12 `positionOf`）。
 * 单挑时按钮兼小盲，故有独立的 'BTN_SB'。
 */
export type Position =
  | 'BTN_SB'
  | 'SB'
  | 'BB'
  | 'UTG'
  | 'UTG1'
  | 'MP'
  | 'MP1'
  | 'HJ'
  | 'CO'
  | 'BTN'

export interface HandConfig {
  readonly handId: HandId
  readonly maxSeats: number // 2..9
  readonly smallBlind: Chips
  readonly bigBlind: Chips // >=2
  readonly anteMode: AnteMode
  readonly ante: Chips
  readonly burnCards: boolean // 默认 true
  readonly showdownPolicy: ShowdownPolicy
  readonly newPlayerPolicy: NewPlayerPolicy
  readonly missedBlindPolicy: MissedBlindPolicy
  /** 快照进手牌，保证回放/统计口径与当时的房间设置一致 */
  readonly assist: AssistPolicy
  /**
   * 玩法。缺省 = 标准（老手牌、老快照都没有这个字段）。
   * 决定牌堆形状（52 / 36 张）与摊牌比大小的规则，一手牌之内不会变。
   */
  readonly deckVariant?: DeckVariant
}

/** 手牌配置里的玩法，缺省按标准 */
export function deckVariantOf(config: { readonly deckVariant?: DeckVariant | undefined }): DeckVariant {
  return config.deckVariant === 'shortDeck' ? 'shortDeck' : 'standard'
}

export interface HandPlayer {
  readonly seat: SeatIndex
  readonly accountId: AccountId
  readonly startingStack: Chips
  stack: Chips
  holeCards: readonly [CardId, CardId]
  status: PlayerHandStatus
  /** 本轮已投入（盲注计入，ante 不计入）——`to` 的语义基准，见红线 #18 */
  committedThisRound: Chips
  /** 本手已投入（ante + 盲注 + 所有下注；未跟注退还后会减少） */
  committedThisHand: Chips
  /** 不完整全下**不**重置其他人的此标志，这是「不重开加注权」的载体 */
  hasActedThisRound: boolean
  lastAction: AppliedActionType | null
  hasShownCards: boolean
}

export interface HandState {
  readonly schemaVersion: number // === HAND_STATE_SCHEMA_VERSION
  readonly config: HandConfig

  // —— 座位与按钮（整手不变）——
  readonly buttonSeat: SeatIndex
  readonly smallBlindSeat: SeatIndex | null // null = dead small blind
  readonly bigBlindSeat: SeatIndex
  /**
   * 本手的小盲**位置**（死小盲时也有；单挑时 = 按钮兼小盲）。房间在手牌 complete 时把它存成
   * 下一手的「上一手小盲位置」，死按钮就靠它定按钮。老快照没有这个字段。
   */
  readonly smallBlindPosition?: SeatIndex
  readonly isHeadsUp: boolean
  readonly deadButton: boolean
  /** 发牌顺序；dealtSeats[0] === 第一个收牌的座位 */
  readonly dealtSeats: readonly SeatIndex[]
  readonly players: Readonly<Record<SeatIndex, HandPlayer>>

  // —— 牌（私密，redact 时整体删除；红线 #1）——
  readonly deck: readonly CardId[] // 52 张
  deckIndex: number
  readonly burned: CardId[]
  readonly board: CardId[] // 0/3/4/5 张，公开

  // —— 阶段与下注轮 ——
  phase: HandPhase
  street: Street | null
  betToMatch: Chips // 本轮最高 committedThisRound
  /** 本轮最后一次「完整」加注的增量；街首重置为 bigBlind（红线 #23） */
  lastFullRaiseSize: Chips
  lastAggressorSeatThisStreet: SeatIndex | null // 街首重置为 null
  actionOn: SeatIndex | null

  // —— 累计 ——
  potTotal: Chips // Σ committedThisHand（退还后）
  pots: readonly Pot[] // 每街结束后重建
  readonly actionLog: AppliedAction[] // 含盲注/ante 条目
  uncalledReturn: { seat: SeatIndex; amount: Chips } | null
  showdown: readonly ShowdownEntry[]
  result: HandResult | null // phase === 'complete' 时非 null
}

// ============================================================================
// 动作（§4.6）
// ============================================================================

/**
 * 玩家意图。`to` 恒为**本轮累计投入总额**（raise-to），不是本次增量（红线 #18）。
 * 例：本轮已投 20、想把总额推到 60，写 `{ type:'raise', to: 60 }`，实扣 40。
 */
export type ActionIntent =
  | { readonly type: 'fold' }
  | { readonly type: 'check' }
  | { readonly type: 'call' }
  | { readonly type: 'bet'; readonly to: Chips }
  | { readonly type: 'raise'; readonly to: Chips }
  /** 引擎归一化为 call / bet / raise */
  | { readonly type: 'allIn' }

/** `source` 不参与任何状态计算，只用于回放与统计的标注 */
export type ActionSource = 'human' | 'bot' | 'timeout' | 'disconnect' | 'admin'

export interface ActionRequest {
  /** 必须 === state.config.handId，否则 WRONG_HAND。此校验必须先于 seq 幂等分支（§4.6） */
  readonly handId: HandId
  /** 服务端必须用连接绑定的座位覆盖，绝不信任客户端上行值（红线 #37） */
  readonly seat: SeatIndex
  readonly intent: ActionIntent
  /** 必须 === state.actionLog.length */
  readonly seq: number
  readonly source: ActionSource
}

export type AppliedActionType =
  | 'fold'
  | 'check'
  | 'call'
  | 'bet'
  | 'raise'
  | 'postSmallBlind'
  | 'postBigBlind'
  | 'postAnte'
  | 'postDead'

export interface AppliedAction {
  readonly index: number // === 在 actionLog 中的下标
  readonly street: Street
  readonly seat: SeatIndex
  readonly type: AppliedActionType
  /** 动作后的 committedThisRound（raise-to 语义，红线 #18） */
  readonly to: Chips
  readonly delta: Chips // 本次从筹码堆扣除的数量
  readonly allIn: boolean
  readonly reopenedAction: boolean // 是否为「完整」下注/加注（重开加注权）
  readonly source: ActionSource

  // —— 回放与统计所需的「动作前」快照，使回放完全不依赖重放引擎 ——
  /** 已收池 + 本街所有人已投入（= UI 上的底池数字） */
  readonly potBefore: Chips
  readonly stackBefore: Chips
  readonly streetInvestedBefore: Chips
  readonly toCallBefore: Chips
  readonly minRaiseToBefore: Chips | null // null = 当时不可加注
  readonly maxRaiseToBefore: Chips
  /** 本街此前下注计数；翻前大盲记为第 1 注（§12.3） */
  readonly betsBefore: number
  readonly activeMask: number // 动作前 status==='active' 的座位位图
  readonly allInMask: number // 动作前 status==='allIn' 的座位位图
}

export type SizingKind = 'third' | 'half' | 'twoThirds' | 'pot' | 'allIn'

export interface SizingPreset {
  readonly kind: SizingKind
  /** raise-to 语义 */
  readonly to: Chips
}

export interface LegalActions {
  readonly seat: SeatIndex
  readonly onAction: boolean
  /** 恒为 true：断线/超时需要统一的 fold 通道，误触由 UI 层拦截（§4.6） */
  readonly canFold: boolean
  readonly canCheck: boolean
  readonly canCall: boolean
  readonly toCall: Chips // 需再投入的增量；0 表示可 check
  readonly callTo: Chips // call 后的 committedThisRound
  readonly callIsAllIn: boolean

  /**
   * ★ 红线 #21：能否加注的**唯一判据**。
   * 绝不允许用 `minRaiseTo < maxRaiseTo` 推断——短码只能全下时两者相等但仍可加注，
   * 而「不完整全下不重开加注权」时两者不等却不可加注，两个方向都会判错。
   */
  readonly canRaise: boolean
  readonly raiseBlockedReason: 'notReopened' | 'noChips' | 'noOpponentCanCall' | null
  readonly raiseKind: 'bet' | 'raise' | null
  readonly minRaiseTo: Chips // 已被 maxRaiseTo 钳位；canRaise=false 时为 0
  readonly minRaiseToFull: Chips // 未钳位的完整最小加注额，用于解释「只能全下」
  readonly maxRaiseTo: Chips // = committedThisRound + stack
  readonly onlyAllIn: boolean // minRaiseToFull > maxRaiseTo
  /** 已收池 + 本街所有人已投入（不含本人待跟） */
  readonly potForSizing: Chips
  readonly sizingPresets: readonly SizingPreset[]
  readonly disabledReason?: EngineErrorCode
}

// ============================================================================
// 底池与结算（§4.8 / §4.18）
// ============================================================================

export interface Pot {
  readonly index: number // 0 = 主池
  readonly amount: Chips
  /** 升序：未弃牌且投入达到 capLevel */
  readonly eligibleSeats: readonly SeatIndex[]
  readonly capLevel: Chips
}

export interface PotAward {
  readonly potIndex: number
  readonly seat: SeatIndex
  readonly amount: Chips
  /** 未摊牌获胜时为 null；对应 entry 被 muck 时也为 null（§4.16-4） */
  readonly handRank: HandRank | null
  /** amount 中来自奇数筹码规则的部分（红线 #24） */
  readonly oddChip: number
}

interface ShowdownEntryBase {
  readonly seat: SeatIndex
  readonly revealOrder: number
}

export interface ShowdownRevealed extends ShowdownEntryBase {
  readonly mucked: false
  readonly holeCards: readonly [CardId, CardId]
  readonly rank: HandRank
}

export interface ShowdownMucked extends ShowdownEntryBase {
  readonly mucked: true
  readonly holeCards: null
  readonly rank: null
}

/**
 * 红线 #3：`mucked === true` 时 `rank` 必须**整体**为 null，连 category 都不给
 * （`HandRank.bestFive` 含实际牌面，泄露 category 也等于泄露牌力信息）。
 *
 * 故意写成判别联合而不是「三个可空字段的 interface」：后者允许构造出
 * `{ mucked: true, rank: someRank }` 这种泄漏结构，只能靠 review 兜住；
 * 判别联合让它在编译期就不成立。
 */
export type ShowdownEntry = ShowdownRevealed | ShowdownMucked

export interface HandResult {
  readonly handId: HandId
  readonly handNo: number
  /** 'fold' 时 showdown 必须为空数组、awards[*].handRank 全为 null（红线 #3） */
  readonly endedBy: 'fold' | 'showdown'

  // —— 以下五项让 HandResult 成为手牌历史/统计的自足单元 ——
  readonly buttonSeat: SeatIndex
  readonly smallBlindSeat: SeatIndex | null
  readonly bigBlindSeat: SeatIndex
  readonly startingStacks: Readonly<Record<SeatIndex, Chips>>
  readonly actionLog: readonly AppliedAction[]

  readonly board: readonly CardId[]
  readonly pots: readonly Pot[]
  readonly awards: readonly PotAward[]
  readonly uncalledReturn: { seat: SeatIndex; amount: Chips } | null
  readonly showdown: readonly ShowdownEntry[]
  /** 不含退还（红线 #19） */
  readonly contributedBySeat: Readonly<Record<SeatIndex, Chips>>
  /** 不含退还（红线 #19） */
  readonly wonBySeat: Readonly<Record<SeatIndex, Chips>>
  readonly netBySeat: Readonly<Record<SeatIndex, number>> // Σ === 0
  readonly finalStacks: Readonly<Record<SeatIndex, Chips>>
}

// ============================================================================
// 事件（§4.17）
// ============================================================================

/** 事件流 = 动画脚本 = 回放数据源。 */
export type HandEvent =
  | {
      readonly t: 'handStarted'
      readonly handId: HandId
      readonly handNo: number
      readonly buttonSeat: SeatIndex
      readonly sbSeat: SeatIndex | null
      readonly bbSeat: SeatIndex
      readonly deadButton: boolean
      readonly players: readonly {
        readonly seat: SeatIndex
        readonly accountId: AccountId
        readonly stack: Chips
        readonly position: Position
      }[]
    }
  | {
      readonly t: 'blindPosted'
      readonly seat: SeatIndex
      readonly kind: 'sb' | 'bb' | 'ante' | 'dead'
      readonly amount: Chips
      readonly allIn: boolean
    }
  | {
      readonly t: 'holeCardsDealt'
      readonly cardsBySeat: Readonly<Record<SeatIndex, readonly [CardId, CardId]>>
    }
  | { readonly t: 'actionOn'; readonly seat: SeatIndex; readonly legal: LegalActions }
  | { readonly t: 'playerActed'; readonly action: AppliedAction }
  | {
      readonly t: 'streetDealt'
      readonly street: Street
      readonly cards: readonly CardId[]
      readonly pacingHintMs: number
    }
  | { readonly t: 'uncalledReturned'; readonly seat: SeatIndex; readonly amount: Chips }
  | { readonly t: 'potsUpdated'; readonly pots: readonly Pot[]; readonly potTotal: Chips }
  | { readonly t: 'runItOut'; readonly remainingStreets: readonly Street[] }
  /** 只在 revealSeat() 里产生，是别人底牌唯一的外泄闸门；恒带两张牌与完整 rank */
  | {
      readonly t: 'cardsRevealed'
      readonly seat: SeatIndex
      readonly cards: readonly [CardId, CardId]
      readonly rank: HandRank
      readonly voluntary: boolean
    }
  | { readonly t: 'cardsMucked'; readonly seat: SeatIndex }
  | { readonly t: 'potAwarded'; readonly award: PotAward }
  | { readonly t: 'handComplete'; readonly result: HandResult }

// ============================================================================
// 错误与结果（§4.19 / §4.14）
// ============================================================================

export type EngineErrorCode =
  | 'INVALID_CONFIG'
  | 'NOT_ENOUGH_PLAYERS'
  | 'INVALID_DECK'
  | 'WRONG_HAND'
  | 'FUTURE_ACTION'
  | 'STALE_ACTION'
  | 'HAND_NOT_IN_BETTING'
  | 'NOT_YOUR_TURN'
  | 'UNKNOWN_SEAT'
  | 'CANNOT_ACT'
  | 'CHECK_NOT_ALLOWED'
  | 'NOTHING_TO_CALL'
  | 'BET_NOT_ALLOWED'
  | 'RAISE_NOT_ALLOWED'
  | 'ACTION_NOT_REOPENED'
  | 'AMOUNT_NOT_INTEGER'
  | 'AMOUNT_BELOW_MIN'
  | 'AMOUNT_ABOVE_STACK'
  | 'AMOUNT_NOT_ABOVE_CURRENT_BET'
  | 'NO_OPPONENT_CAN_CALL'

export interface EngineError {
  readonly code: EngineErrorCode
  readonly message: string
  readonly seat?: SeatIndex
}

/** 铁律 #4：所有外部输入的失败一律走这里，不抛异常。 */
export type EngineResult<T> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly error: EngineError }

export function engineOk<T>(value: T): EngineResult<T> {
  return { ok: true, value }
}

export function engineErr(
  code: EngineErrorCode,
  message: string,
  seat?: SeatIndex,
): EngineResult<never> {
  // exactOptionalPropertyTypes 下不能写 { seat: undefined }，故分支构造
  return {
    ok: false,
    error: seat === undefined ? { code, message } : { code, message, seat },
  }
}

/**
 * 铁律 #4 的另一半：只有「引擎内部不变量被破坏」才抛。
 * 外部输入不合法永远不该走到这里——走到了就是引擎自己有 bug。
 */
export class InvariantError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'InvariantError'
  }
}
